/* Pure permission-gating helpers for the dashboard widget catalog.
 * No React import — safe to unit-test in a plain node environment.
 * Nothing role-named: gating is by permission key only. */
import type { WidgetDef, WidgetLayoutEntry, WidgetSize } from '../WidgetGrid';
import { hasPermission, hasAnyPermission } from '../../../shared/permissions';

/** Widgets the given permission set is allowed to see — a def with no
 *  `requires` is always visible; otherwise every listed key must pass.
 *  `requiresAny` (any-of) is AND-ed with `requires` — for permission
 *  families with no implication map (e.g. forecasting.view_own/_team/_all
 *  are exact-match keys, not tiers). */
export function visibleWidgets<D extends WidgetDef<any>>(
  defs: D[],
  permissions: string[] | undefined
): D[] {
  return defs.filter(
    (d) =>
      (!d.requires || d.requires.every((k) => hasPermission(permissions, k))) &&
      (!d.requiresAny || hasAnyPermission(permissions, d.requiresAny))
  );
}

/** Column span (of the 12-col grid) each legacy size tier maps to. Grid
 *  units for react-grid-layout, not CSS. */
export const SIZE_COLS: Record<WidgetSize, number> = { compact: 3, standard: 6, expanded: 12 };

/** Width → tier breakpoint: a widget's rendered tier is derived from the
 *  columns it spans (≤3 compact, 4–8 standard, ≥9 expanded). */
export function tierFor(cols: number): WidgetSize {
  if (cols <= 3) return 'compact';
  if (cols <= 8) return 'standard';
  return 'expanded';
}

/** Default layout for a fresh dashboard: every visible widget, in def
 *  order, at its default width (the column span of its default size,
 *  falling back to 'standard' = 6 cols). No x/y is set — WidgetGrid
 *  auto-places entries with no stored position. */
export function defaultLayoutFor<C>(
  defs: WidgetDef<C>[],
  permissions: string[] | undefined
): WidgetLayoutEntry[] {
  return visibleWidgets(defs, permissions).map((d) => ({
    id: d.id,
    w: SIZE_COLS[d.defaultSize ?? 'standard'],
  }));
}

/** A firm-wide starter layout (FirmPrefs `dashboard.starter.<surface>`),
 *  filtered through the widgets the CURRENT viewer's permissions already
 *  allow — a starter saved by an admin can never hand a colleague a widget
 *  they lack the key for. Order preserved; ids not in `permittedDefs` (a
 *  hidden widget, or one since removed from the catalog) are dropped. Pure. */
export function applyFirmStarter<C>(
  starter: WidgetLayoutEntry[] | null | undefined,
  permittedDefs: WidgetDef<C>[]
): WidgetLayoutEntry[] {
  if (!Array.isArray(starter) || starter.length === 0) return [];
  const allowedIds = new Set(permittedDefs.map((d) => d.id));
  return starter.filter((e) => allowedIds.has(e.id));
}
